import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import {
  useTradingIntent,
  INTENT_BOT_TYPES,
} from "features/onboarding/guided/intent";
import {
  ONBOARDING_TRADING_METHOD_PATH,
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  BASE_BOTS_WITH_ACTION_PATH,
  BASE_PUBLIC_EDIT_BOT_PATH,
  BULK_CREATE_BOTS_FEATURE_FLAG,
} from "common/constants";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  BotConfigurationType,
  BotType,
  QueryBotType,
  WorkspaceType,
} from "@shared/types";
import {
  APPLIED_BACKTESTING_QUERY_PARAM,
  BACKTESTING_BOT_QUERY_PARAM,
  ACCOUNT_ID_QUERY_PARAM,
  COPY_TO_WORKSPACE_QUERY_PARAM,
  DRAWER_BACKTESTING_QUERY_PARAM,
  GROUP_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import useQuery from "utils/hooks/useQuery";
import { useUser } from "@shared/utils/hooks/misc";
import { getBacktetingExtraDetailsRoute, useQueryGroupId } from "utils/routes";
import {
  ENV_TYPE_QUERY_PARAM,
  QUERY_BOT_TYPE_TO_BOT_TYPE,
} from "@shared/common/constants";
import { BacktestingExtraDetailsTabs } from "@shared/features/bots/types";

export const useQueryBotType = () => {
  const { user } = useUser();
  const query = useQuery();
  const generalMatch = useRouteMatch(BASE_BOTS_WITH_ACTION_PATH);
  const generalQueryData = (generalMatch?.params || {}) as {
    botType?: string;
    action?: string;
    botId?: string;
  };
  const publicEditMatch = useRouteMatch(BASE_PUBLIC_EDIT_BOT_PATH);
  const { botType: publicEditQueryBotType, botUUID } =
    (publicEditMatch?.params || {}) as {
      botType?: string;
      botUUID?: string;
    };
  const adminTemplateMatch = useRouteMatch(
    ADMIN_TRADING_CONFIGURATION_FORM_PATH
  );
  const { configurationType } = (adminTemplateMatch?.params || {}) as {
    configurationType?: BotConfigurationType;
  };
  const subGroupIdStr = query.get(GROUP_ID_QUERY_PARAM) || "";
  const querySubGroupId = useQueryGroupId();
  const subGroupId =
    querySubGroupId ?? (subGroupIdStr ? Number(subGroupIdStr) : undefined);
  let queryBotType: string | undefined;
  if (adminTemplateMatch) queryBotType = QueryBotType.SIGNAL;
  else {
    queryBotType = generalQueryData.botType || publicEditQueryBotType;
  }
  const queryAction = generalQueryData.action;
  const queryBotId = generalQueryData.botId;
  const backtestingBotId = query.get(BACKTESTING_BOT_QUERY_PARAM);
  const backtestingId = query.get(APPLIED_BACKTESTING_QUERY_PARAM);
  const isBacktesting = !!backtestingBotId && !!backtestingId;
  const queryIsCopyToWorkspace = query.get(COPY_TO_WORKSPACE_QUERY_PARAM);
  const isCopyToWorkspace = queryIsCopyToWorkspace === "true";

  const onboardingMatch = useRouteMatch(ONBOARDING_TRADING_METHOD_PATH);
  const { intent } = useTradingIntent();
  // Onboarding: "Customize settings" on a DCA preset opens the setup form as
  // a copy of that public bot (its uuid in the query).
  const onboardingCopyUUID =
    onboardingMatch && query.get("setup") === "1" && intent === "dca"
      ? query.get("botUUID") || undefined
      : undefined;
  const workspace = useSelector(selectWorkspace);
  const botType: BotType =
    onboardingMatch && query.get("setup") === "1" && intent
      ? INTENT_BOT_TYPES[intent]
      : QUERY_BOT_TYPE_TO_BOT_TYPE[queryBotType || QueryBotType.ASAP];

  const getBulkEditBotIDs = () => {
    try {
      const queryBotIDs = query.get("botIds");
      return queryBotIDs ? JSON.parse(queryBotIDs) : undefined;
    } catch {}
  };
  const botIDs = getBulkEditBotIDs();

  const getBotFormType = () => {
    let botFormType = BotFormType.CREATE;
    if (publicEditQueryBotType || onboardingCopyUUID)
      botFormType = BotFormType.COPY_PUBLIC;
    else if (adminTemplateMatch) botFormType = BotFormType.EDIT;
    else if (queryAction === "duplicate") botFormType = BotFormType.DUPLICATE;
    else if (queryAction === "edit") {
      if (botIDs) botFormType = BotFormType.BULK_EDIT;
      else botFormType = BotFormType.EDIT;
    } else if (
      queryAction === "bulk_create" &&
      (user?.hasFeatureFlag(BULK_CREATE_BOTS_FEATURE_FLAG) ||
        user?.isAssetManager()) &&
      botType !== BotType.SIGNAL
    )
      botFormType = BotFormType.BULK_CREATE;
    return botFormType;
  };

  const botFormType = getBotFormType();
  const isDemoCopyPublic =
    botFormType === BotFormType.COPY_PUBLIC &&
    (query.get(ENV_TYPE_QUERY_PARAM) === WorkspaceType.DEMO ||
      (!!onboardingCopyUUID && workspace === WorkspaceType.DEMO));

  return {
    botType,
    botIdStr: queryBotId,
    botUUID: botUUID ?? onboardingCopyUUID,
    botIDs,
    botFormType,
    isChannelConfiguration: !!adminTemplateMatch,
    isBacktesting,
    backtestingBotId,
    isCopyToWorkspace,
    isDemoCopyPublic,
    configurationType,
    subGroupId,
  };
};

export const useNavigateToBacktestDetails = () => {
  const history = useHistory();
  const query = useQuery();

  const navigateToBacktestDetails = (
    botId: number,
    backtestId: number,
    subGroupId?: number,
    accountId?: number
  ) => {
    const extraDetailsRoute = getBacktetingExtraDetailsRoute(
      location.pathname,
      botId.toString(),
      BacktestingExtraDetailsTabs.GENERAL
    );
    const updatedQuery = new URLSearchParams(query.toString());
    updatedQuery.set(DRAWER_BACKTESTING_QUERY_PARAM, backtestId.toString());

    if (subGroupId) {
      updatedQuery.set(GROUP_ID_QUERY_PARAM, subGroupId.toString());
    }
    if (accountId) {
      updatedQuery.set(ACCOUNT_ID_QUERY_PARAM, accountId.toString());
    }

    history.push(`${extraDetailsRoute}?${updatedQuery.toString()}`);
  };

  return {
    navigateToBacktestDetails,
  };
};
